import { useTranslation } from 'react-i18next';
import { ExternalLink, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/layout/PageHeader';
import { usePartnerContact } from './portal-api';

function lineAddUrl(lineId: string): string {
  return `https://line.me/R/ti/p/${encodeURIComponent(lineId)}`;
}

/** The brand's LINE channel as a link, or that none is set. Also rendered for a pending partner, which the API allows. */
export function PartnerContactLine() {
  const { t } = useTranslation();
  const query = usePartnerContact();

  if (query.isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {t('common.loading')}
      </p>
    );
  }
  if (query.error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('partnerPortal.contact.loadFailed')}
      </p>
    );
  }
  const lineId = query.data?.lineId ?? null;
  if (!lineId) return <p className="text-sm text-muted-foreground">{t('partnerPortal.contact.none')}</p>;
  return (
    <a
      href={lineAddUrl(lineId)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {lineId}
      <ExternalLink className="h-4 w-4" aria-hidden />
      <span className="sr-only">{t('partnerPortal.contact.opensNewTab')}</span>
    </a>
  );
}

export function PartnerContactPage() {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <PageHeader title={t('partnerPortal.contact.title')} description={t('partnerPortal.contact.description')} />
      <section aria-labelledby="partner-contact-line">
        <Card>
          <CardHeader>
            <CardTitle id="partner-contact-line" className="flex items-center gap-2 text-base">
              <MessageCircle className="h-4 w-4" aria-hidden />
              {t('partnerPortal.contact.line')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PartnerContactLine />
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
